import { serverMutationHeaders } from '../auth/serverSession'
import {
  APPLICATION_CHAT_CONTROL_REF_RE,
  normalizeApplicationChatControls,
} from '../../shared/applicationChatControls'
import {
  ChatIntentJournal,
  type ChatIntentScope,
  type ChatIntentStorage,
} from './chatIntentJournal'
import { MutationIntentJournal } from './mutationIntentJournal'
import type { IApiClient, RemoteProjectionSnapshot, StreamHandle } from './interface'
import type { RemoteEventPage } from './interface'
import type {
  DaemonHealth,
  DaemonPluginHealth,
  Interaction,
  InteractionAnswer,
  KnowledgeEntry,
  NormalizedPhase,
  NormalizedRun,
  QueueEntry,
  ProjectedAgent,
  ProjectedAgentDetail,
  ProjectedChat,
  ProjectedChatMessage,
  ProjectedChatTurn,
  ApplicationChatControls,
  AllowedApplicationChatControls,
  ChatSendResult,
  RunStatus,
  Task,
  TranscriptEvent,
  OperationsCostSummary,
} from './types'

const BASE = '/api/application/v1'
const RUN_STATUSES = new Set<RunStatus>([
  'queued',
  'running',
  'completed',
  'failed',
  'cancelled',
])

interface ApplicationResource {
  id: string
  type: string
  workspace_id?: string
  owner_id?: string
  initiated_by?: string
  assigned_to?: string
  visibility?: 'private' | 'org' | 'public'
  audience?: string[]
  allowed_actions?: string[]
  attributes?: Record<string, unknown>
}

interface Envelope {
  schema: 'portal.application.v1'
  data?: Record<string, unknown>
  cursor?: string
  reset?: boolean
  events?: unknown[]
}

interface ArenaProjectionEnvelope {
  schema: 'arena.application-projection.v1'
  snapshot_id?: unknown
  captured_at?: unknown
  scope?: unknown
  data?: unknown
}

export class ApplicationHttpError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    readonly code?: string,
  ) {
    super(`Application request ${path} failed (${status})`)
    this.name = 'ApplicationHttpError'
  }
}

export function isDefinitiveMutationFailure(error: unknown): boolean {
  if (!(error instanceof ApplicationHttpError)) return false
  if (error.code === 'idempotency_in_progress') return false
  if ([408, 425, 429].includes(error.status)) return false
  if (error.status === 409 && error.code !== 'idempotency_conflict') return false
  return error.status >= 400 && error.status < 500
}

export interface ApplicationApiClientOptions {
  chatIntentScope?: ChatIntentScope
  chatIntentStorage?: ChatIntentStorage
  now?: () => number
  chatIntentTtlMs?: number
  chatIntentCapacity?: number
  pruneForeignChatIntentPartitions?: boolean
  agentChatRetryDelaysMs?: readonly number[]
  wait?: (delayMs: number) => Promise<void>
}

const DEFAULT_AGENT_CHAT_RETRY_DELAYS_MS = [250, 750, 1_500] as const

function isAgentChatConfirmationPending(error: unknown): boolean {
  return error instanceof ApplicationHttpError && (
    error.code === 'chat_creation_unconfirmed' ||
    error.code === 'idempotency_in_progress'
  )
}

export function extractSseFrames(buffer: string): {
  frames: string[]
  remainder: string
} {
  const frames: string[] = []
  let remainder = buffer
  let match = /\r?\n\r?\n/.exec(remainder)
  while (match?.index !== undefined) {
    frames.push(remainder.slice(0, match.index))
    remainder = remainder.slice(match.index + match[0].length)
    match = /\r?\n\r?\n/.exec(remainder)
  }
  return { frames, remainder }
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? value as Record<string, unknown>
    : {}
}

function string(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

const PLUGIN_STATUSES = new Set<DaemonPluginHealth['status']>([
  'healthy',
  'degraded',
  'down',
  'unknown',
])
const MAX_TRUSTED_OPERATION_PLUGINS = 64

function boundedText(value: unknown, maximum: number): string | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim()
  return normalized.length > 0 && normalized.length <= maximum ? normalized : undefined
}

function finiteNonNegative(
  value: unknown,
  integer = false,
  maximum = 1_000_000_000,
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > maximum) {
    return undefined
  }
  if (integer && (!Number.isSafeInteger(value) || value < 0)) return undefined
  return value
}

/** Parse the whole bounded field or discard it; never display a partial plugin identity set. */
function operationsPlugins(value: unknown): DaemonPluginHealth[] | undefined {
  if (!Array.isArray(value) || value.length > MAX_TRUSTED_OPERATION_PLUGINS) return undefined
  const parsed: DaemonPluginHealth[] = []
  const identities = new Set<string>()
  for (const candidate of value) {
    const plugin = record(candidate)
    const name = boundedText(plugin.name, 80)
    const kind = boundedText(plugin.kind, 48)
    const pluginStatus = plugin.status
    if (!name || !kind || !PLUGIN_STATUSES.has(pluginStatus as DaemonPluginHealth['status'])) {
      return undefined
    }
    const identity = `${kind}\0${name}`
    if (identities.has(identity)) return undefined
    identities.add(identity)
    parsed.push({ name, kind, status: pluginStatus as DaemonPluginHealth['status'] })
  }
  return parsed
}

/** Strictly consume only the trusted aggregate fields rendered by Arena. */
function operationsCostSummary(value: unknown): OperationsCostSummary | undefined {
  const summary = record(value)
  const since = boundedText(summary.since, 24)
  const windowStart = boundedText(summary.window_start, 40)
  const windowEnd = boundedText(summary.window_end, 40)
  const totalTokens = finiteNonNegative(summary.total_tokens, true, 1_000_000_000_000_000)
  const totalCostUsd = finiteNonNegative(summary.total_cost_usd)
  const reportedUsd = finiteNonNegative(summary.reported_usd)
  const estimatedUsd = finiteNonNegative(summary.estimated_usd)
  const start = windowStart ? Date.parse(windowStart) : Number.NaN
  const end = windowEnd ? Date.parse(windowEnd) : Number.NaN
  if (
    since !== '24h' || !windowStart || !windowEnd ||
    (summary.windowed !== true && summary.windowed !== false) ||
    totalTokens === undefined || totalCostUsd === undefined || reportedUsd === undefined ||
    estimatedUsd === undefined || !Number.isFinite(start) || !Number.isFinite(end) || end < start
  ) return undefined
  return {
    since,
    windowStart,
    windowEnd,
    windowed: summary.windowed,
    totalTokens,
    totalCostUsd,
    reportedUsd,
    estimatedUsd,
  }
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function status(value: unknown): RunStatus {
  return RUN_STATUSES.has(value as RunStatus) ? value as RunStatus : 'running'
}

function resource(value: unknown): ApplicationResource {
  const item = record(value)
  return {
    id: string(item.id),
    type: string(item.type),
    workspace_id: string(item.workspace_id) || undefined,
    owner_id: string(item.owner_id) || undefined,
    initiated_by: string(item.initiated_by) || undefined,
    assigned_to: string(item.assigned_to) || undefined,
    visibility:
      item.visibility === 'private' ||
      item.visibility === 'org' ||
      item.visibility === 'public'
        ? item.visibility
        : undefined,
    audience: Array.isArray(item.audience)
      ? item.audience.filter((id): id is string => typeof id === 'string')
      : [],
    attributes: record(item.attributes),
    allowed_actions: Array.isArray(item.allowed_actions)
      ? item.allowed_actions.filter((action): action is string => typeof action === 'string')
      : [],
  }
}

function normalizeRun(value: unknown): NormalizedRun {
  const item = resource(value)
  const attributes = item.attributes ?? {}
  const phases = Array.isArray(attributes.phases)
    ? attributes.phases.map((phaseValue): NormalizedPhase => {
        const phase = record(phaseValue)
        return {
          id: string(phase.id ?? phase.phase_id),
          title: string(phase.title ?? phase.id ?? phase.phase_id, 'Phase'),
          status: status(phase.status),
          startedAt: string(phase.started_at) || undefined,
          finishedAt: string(phase.finished_at ?? phase.completed_at) || undefined,
          agentCount: Number(phase.agent_count ?? 0),
        }
      })
    : []
  const createdAt = string(
    attributes.created_at ?? attributes.started_at,
    new Date(0).toISOString(),
  )
  return {
    id: item.id,
    taskId: string(attributes.subject_id ?? attributes.task_id, item.id),
    title: string(attributes.title ?? record(attributes.subject).title, item.id),
    status: status(attributes.status),
    currentPhase: string(attributes.current_phase) || undefined,
    phases,
    activeAgents: Number(attributes.active_agents ?? 0),
    createdAt,
    updatedAt: string(
      attributes.updated_at ?? attributes.finished_at,
      createdAt,
    ),
    durationMs:
      typeof attributes.duration_ms === 'number'
        ? attributes.duration_ms
        : undefined,
    agentId: string(attributes.agent_id) || undefined,
    agentName: string(attributes.agent_name) || undefined,
  }
}

function normalizeInteraction(value: unknown): Interaction {
  const item = resource(value)
  const attributes = item.attributes ?? {}
  const rawType = string(attributes.kind ?? attributes.type)
  const type: Interaction['type'] =
    rawType === 'approval' ? 'approval' : rawType === 'question' ? 'question' : 'input'
  return {
    id: item.id,
    runId: string(attributes.run_id ?? attributes.workflow_id),
    agentId: string(attributes.agent_id) || undefined,
    type,
    prompt: string(attributes.prompt ?? attributes.question ?? attributes.action),
    createdAt: string(attributes.created_at, new Date(0).toISOString()),
    timeoutAt: string(attributes.timeout_at) || undefined,
    toolName: string(attributes.tool_name) || undefined,
    toolArguments: record(attributes.arguments),
    questions: Array.isArray(attributes.questions)
      ? attributes.questions as Interaction['questions']
      : undefined,
  }
}

function normalizeAgent(value: unknown): ProjectedAgent {
  const item = resource(value)
  return {
    id: item.id,
    name: string(item.attributes?.name, item.id),
    ...(item.allowed_actions?.includes('start_chat') ? { canStartChat: true } : {}),
  }
}

function normalizeTask(value: unknown): Task {
  const item = resource(value)
  const attributes = item.attributes ?? {}
  return {
    id: item.id,
    title: string(attributes.title, item.id),
    status: string(attributes.status, 'ready'),
    priority: string(attributes.priority) || undefined,
    labels: Array.isArray(attributes.labels)
      ? attributes.labels.filter((label): label is string => typeof label === 'string')
      : undefined,
    assignee: item.assigned_to || undefined,
    canLaunch: item.allowed_actions?.includes('launch') ?? false,
  }
}

function normalizeKnowledge(value: unknown): KnowledgeEntry {
  const item = resource(value)
  const attributes = item.attributes ?? {}
  const relationships = Array.isArray(attributes.relationships)
    ? attributes.relationships
      .map(record)
      .filter((relationship) => typeof relationship.to_id === 'string')
      .map((relationship) => ({
        to_id: string(relationship.to_id),
        type: string(relationship.type, 'related'),
        confidence:
          typeof relationship.confidence === 'number'
            ? Math.max(0, Math.min(1, relationship.confidence))
            : 0.5,
      }))
    : []
  return {
    id: item.id,
    title: string(attributes.title, item.id),
    entityKind: string(attributes.entity_kind ?? attributes.kind, 'knowledge'),
    relationships,
  }
}

function normalizeQueueEntry(value: unknown, index: number): QueueEntry {
  const item = resource(value)
  const attributes = item.attributes ?? {}
  return {
    id: item.id,
    taskId: string(attributes.subject_id, item.id),
    title: string(attributes.title ?? attributes.subject_id, item.id),
    position: Number(attributes.position ?? index + 1),
    status:
      attributes.status === 'dispatched' || attributes.status === 'running'
        ? attributes.status
        : 'pending',
    submittedAt: string(
      attributes.enqueued_at,
      new Date(0).toISOString(),
    ),
  }
}

function normalizeDaemonHealth(value: unknown): DaemonHealth {
  const operation = resource(value)
  const health = record(record(operation.attributes).health)
  const attributes = record(operation.attributes)
  const operationStatus = record(attributes.status)
  const healthy = health.healthy
  const healthStatus = health.status
  const plugins = operationsPlugins(attributes.plugins)
  const costSummary = operationsCostSummary(attributes.cost_summary)
  return {
    status: healthy === true || healthStatus === 'healthy'
      ? 'healthy'
      : healthy === false || healthStatus === 'down'
        ? 'down'
        : 'degraded',
    uptime: finiteNonNegative(operationStatus.uptime_seconds) ?? 0,
    activeRuns: Number(health.active_runs ?? 0),
    queueDepth: Number(health.queued_tasks ?? 0),
    activeAgents: Number(health.active_agents ?? 0),
    version: string(operationStatus.version ?? health.version),
    checkedAt: new Date().toISOString(),
    ...(plugins ? { plugins } : {}),
    ...(costSummary ? { costSummary } : {}),
  }
}

const MAX_PROJECTED_CHAT_MESSAGES = 200
const MAX_PROJECTED_CHAT_CONTENT_CODEPOINTS = 32_000
const MAX_PROJECTED_CHAT_CONTENT_BYTES = 64 * 1024
const MAX_PROJECTED_CHAT_CATALOG = 1_000
const PROJECTED_CHAT_PAGE_SIZE = 200

function normalizedProjectedChatContent(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.replace(/\r\n?/g, '\n').trim()
  const codepoints = Array.from(normalized)
  if (
    !normalized ||
    codepoints.length > MAX_PROJECTED_CHAT_CONTENT_CODEPOINTS ||
    new TextEncoder().encode(normalized).byteLength > MAX_PROJECTED_CHAT_CONTENT_BYTES ||
    /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(normalized) ||
    codepoints.some((character) => {
      const point = character.codePointAt(0) ?? 0
      return point >= 0xd800 && point <= 0xdfff
    })
  ) return null
  return normalized
}

/** Canonical browser-side validation shared by the composer and HTTP client. */
export function normalizeChatSendText(value: string): string | null {
  const normalized = value.replace(/\r\n?/g, '\n').trim()
  const codepoints = Array.from(normalized)
  if (
    !normalized ||
    codepoints.length > 4_000 ||
    new TextEncoder().encode(normalized).byteLength > 8 * 1024 ||
    /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(normalized) ||
    codepoints.some((character) => {
      const point = character.codePointAt(0) ?? 0
      return point >= 0xd800 && point <= 0xdfff
    })
  ) return null
  return normalized
}

function normalizeChatMessage(value: unknown): ProjectedChatMessage | null {
  const message = record(value)
  const content = normalizedProjectedChatContent(message.content)
  if (!content || (message.role !== 'user' && message.role !== 'assistant')) return null
  const seq = finiteNonNegative(message.seq, true, Number.MAX_SAFE_INTEGER)
  return {
    content,
    id: boundedText(message.id, 256),
    role: message.role,
    createdAt: boundedText(message.created_at, 64),
    timestamp: boundedText(message.timestamp, 64),
    seq,
  }
}

function canonicalChatAgentId(value: unknown): string | undefined | null {
  if (value === undefined || value === null) return undefined
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value !== value.trim() ||
    value.startsWith('-') ||
    new TextEncoder().encode(value).byteLength > 512 ||
    /[\u0000-\u001f\u007f]/.test(value)
  ) return null
  return value
}

const CHAT_REASONING_EFFORTS = ['low', 'medium', 'high'] as const
const CHAT_PERMISSION_INTENTS = ['default', 'review', 'auto_edit', 'unrestricted'] as const

function exactControlOptions<T extends string>(
  value: unknown,
  allowed: readonly T[],
  maximum = allowed.length,
): T[] | null {
  if (!Array.isArray(value) || value.length > maximum) return null
  const options: T[] = []
  for (const option of value) {
    if (typeof option !== 'string' || !allowed.includes(option as T) || options.includes(option as T)) return null
    options.push(option as T)
  }
  return options
}

function normalizeAllowedChatControls(value: unknown): AllowedApplicationChatControls | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  if (
    source.schema !== 'animus.chat.application_controls.v1' ||
    Object.keys(source).some((key) => ![
      'schema', 'approvals', 'reasoning_effort', 'permission_intent', 'profile_ref', 'skill_ref',
    ].includes(key))
  ) return null
  const approvals = Array.isArray(source.approvals)
    && source.approvals.length <= 2
    && source.approvals.every((option) => typeof option === 'boolean')
    && new Set(source.approvals).size === source.approvals.length
      ? source.approvals as boolean[]
      : null
  const reasoning = exactControlOptions(source.reasoning_effort, CHAT_REASONING_EFFORTS)
  const permissions = exactControlOptions(source.permission_intent, CHAT_PERMISSION_INTENTS)
  const refs = (candidate: unknown, maximum: number): string[] | null => {
    if (!Array.isArray(candidate) || candidate.length > maximum) return null
    const result: string[] = []
    for (const ref of candidate) {
      if (typeof ref !== 'string' || !APPLICATION_CHAT_CONTROL_REF_RE.test(ref) || result.includes(ref)) return null
      result.push(ref)
    }
    return result
  }
  const profiles = refs(source.profile_ref, 1)
  const skills = refs(source.skill_ref, 100)
  if (!approvals || !reasoning || !permissions || !profiles || !skills) return null
  return {
    schema: 'animus.chat.application_controls.v1',
    approvals,
    reasoning_effort: reasoning,
    permission_intent: permissions,
    profile_ref: profiles,
    skill_ref: skills,
  }
}

function authorizedChatControls(
  controls: ApplicationChatControls | undefined,
  allowed: AllowedApplicationChatControls | undefined,
): ApplicationChatControls | undefined {
  if (!controls) return undefined
  const normalized = normalizeApplicationChatControls(controls)
  if (!normalized) throw new Error('Invalid chat controls')
  if (Object.keys(normalized).length === 1) return undefined
  if (!allowed || normalized.schema !== allowed.schema) throw new Error('Chat controls are not authorized')
  if (normalized.approvals !== undefined && !allowed.approvals.includes(normalized.approvals)) {
    throw new Error('Chat controls are not authorized')
  }
  if (normalized.reasoning_effort !== undefined && !allowed.reasoning_effort.includes(normalized.reasoning_effort)) {
    throw new Error('Chat controls are not authorized')
  }
  if (normalized.permission_intent !== undefined && !allowed.permission_intent.includes(normalized.permission_intent)) {
    throw new Error('Chat controls are not authorized')
  }
  if (normalized.profile_ref !== undefined && !allowed.profile_ref.includes(normalized.profile_ref)) {
    throw new Error('Chat controls are not authorized')
  }
  if (normalized.skill_ref !== undefined && !allowed.skill_ref.includes(normalized.skill_ref)) {
    throw new Error('Chat controls are not authorized')
  }
  return normalized as ApplicationChatControls
}

function normalizeChat(value: unknown): ProjectedChat {
  const item = resource(value)
  if (item.type !== 'chat' || !item.id) throw new Error('Invalid chat projection')
  const attributes = item.attributes ?? {}
  const rawMessages = attributes.messages
  if (
    rawMessages !== undefined &&
    (!Array.isArray(rawMessages) || rawMessages.length > MAX_PROJECTED_CHAT_MESSAGES)
  ) {
    throw new Error('Invalid chat message projection')
  }
  const messages = Array.isArray(rawMessages)
    ? rawMessages.map(normalizeChatMessage).filter((message): message is ProjectedChatMessage => message !== null)
    : undefined
  const messageCount = finiteNonNegative(attributes.message_count, true, Number.MAX_SAFE_INTEGER)
  const revision = finiteNonNegative(attributes.revision, true, Number.MAX_SAFE_INTEGER)
  const agentId = canonicalChatAgentId(attributes.agent_id)
  const allowedControls = attributes.allowed_controls === undefined
    ? undefined
    : normalizeAllowedChatControls(attributes.allowed_controls)
  if (revision === undefined || agentId === null || allowedControls === null) {
    throw new Error('Invalid canonical chat binding')
  }
  return {
    id: item.id,
    title: boundedText(attributes.title, 256) ?? 'Conversation',
    ...(agentId ? { agentId } : {}),
    revision,
    canSend: item.allowed_actions?.includes('send') ?? false,
    ...(allowedControls ? { allowedControls } : {}),
    messageCount,
    createdAt: boundedText(attributes.created_at, 64),
    updatedAt: boundedText(attributes.updated_at, 64),
    lastMessageAt: boundedText(attributes.last_message_at, 64),
    ...(messages ? { messages } : {}),
  }
}

function receiptString(value: unknown): string | null {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 512 &&
    !/[\u0000-\u001f\u007f]/.test(value)
    ? value
    : null
}

function canonicalReceiptMessage(
  chat: ProjectedChat,
  candidate: ProjectedChatMessage | null,
  identity: { id: string; seq: number; role: ProjectedChatMessage['role'] },
): ProjectedChatMessage | null {
  if (!candidate || !chat.messages) return null
  if (
    candidate.role !== identity.role ||
    candidate.id !== identity.id ||
    candidate.seq !== identity.seq
  ) return null
  const matches = chat.messages.filter((message) => {
    return message.id === identity.id &&
      message.seq === identity.seq &&
      message.id === candidate.id &&
      message.seq === candidate.seq &&
      message.role === candidate.role &&
      message.content === candidate.content &&
      message.createdAt === candidate.createdAt &&
      message.timestamp === candidate.timestamp
  })
  return matches.length === 1 ? matches[0] : null
}

function canonicalChatTurn(
  value: unknown,
  binding: { agentId?: string; revision: number },
): ProjectedChatTurn | null {
  const turn = record(value)
  const status = turn.status
  if (
    status !== 'completed' &&
    status !== 'assistant_failed' &&
    status !== 'assistant_interrupted'
  ) return null
  if (!Object.prototype.hasOwnProperty.call(turn, 'agent_id')) return null
  const agentId = canonicalChatAgentId(turn.agent_id)
  const expectedRevision = finiteNonNegative(
    turn.expected_revision,
    true,
    Number.MAX_SAFE_INTEGER,
  )
  const operationId = receiptString(turn.operation_id)
  const userMessageId = receiptString(turn.user_message_id)
  const userSeq = finiteNonNegative(turn.user_seq, true, Number.MAX_SAFE_INTEGER)
  if (
    agentId === null ||
    agentId !== binding.agentId ||
    expectedRevision !== binding.revision ||
    !operationId ||
    !userMessageId ||
    userSeq === undefined
  ) return null

  if (status === 'completed') {
    const messageId = receiptString(turn.message_id)
    const seq = finiteNonNegative(turn.seq, true, Number.MAX_SAFE_INTEGER)
    if (!messageId || seq === undefined) return null
    return {
      status,
      ...(agentId ? { agentId } : {}),
      expectedRevision,
      operationId,
      userMessageId,
      userSeq,
      messageId,
      seq,
    }
  }
  const error = record(turn.error)
  if (error.code !== status || !receiptString(error.message)) return null
  return {
    status,
    ...(agentId ? { agentId } : {}),
    expectedRevision,
    operationId,
    userMessageId,
    userSeq,
  }
}

async function applicationFetch(path: string, init?: RequestInit): Promise<Envelope> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.method === 'POST' ? serverMutationHeaders() : {}),
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null)
    const code = boundedText(record(record(errorPayload).error).code, 128)
    throw new ApplicationHttpError(response.status, path, code)
  }
  const payload = await response.json() as Envelope
  if (payload.schema !== 'portal.application.v1') {
    throw new Error('Portal application schema mismatch')
  }
  return payload
}

async function projectionFetch(): Promise<ArenaProjectionEnvelope> {
  const path = '/api/v1/application-projection'
  const response = await fetch(path, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null)
    const code = boundedText(record(record(errorPayload).error).code, 128)
    throw new ApplicationHttpError(response.status, path, code)
  }
  const payload = await response.json() as ArenaProjectionEnvelope
  if (payload.schema !== 'arena.application-projection.v1') {
    throw new Error('Arena application projection schema mismatch')
  }
  return payload
}

function projectedArray(
  data: Record<string, unknown>,
  field: string,
  maximum: number,
): unknown[] {
  const values = data[field]
  if (!Array.isArray(values) || values.length > maximum) {
    throw new Error(`Arena application projection field ${field} is invalid`)
  }
  return values
}

function eventToTranscript(value: unknown, runId: string): TranscriptEvent {
  const event = record(value)
  const attributes = record(event.attributes)
  return {
    id: string(event.id ?? event.cursor),
    runId,
    phaseId: string(attributes.phase_id ?? event.phase_id) || undefined,
    agentId: string(attributes.agent_id ?? event.agent_id) || undefined,
    type: string(attributes.type ?? event.type, 'text') as TranscriptEvent['type'],
    content: string(attributes.content ?? event.content),
    metadata: record(attributes.metadata ?? event.metadata),
    timestamp: string(
      attributes.timestamp ?? event.timestamp,
      new Date().toISOString(),
    ),
  }
}

/**
 * Browser client for Portal's deliberately narrow, generic application
 * surface. It has no bearer token, Portal base URL, GraphQL client, or Portal
 * domain-specific endpoint knowledge.
 */
export class ApplicationApiClient implements IApiClient {
  private readonly actions = new Map<string, ReadonlySet<string>>()
  private readonly chatBindings = new Map<string, { agentId?: string; revision: number }>()
  private readonly chatControlPolicies = new Map<string, AllowedApplicationChatControls>()
  private readonly chatIntents: ChatIntentJournal
  private readonly mutationIntents: MutationIntentJournal
  private readonly scope: ChatIntentScope
  private readonly projectionScope?: Pick<ChatIntentScope, 'userId' | 'workspaceId' | 'projectId'>
  private readonly agentChatRetryDelaysMs: readonly number[]
  private readonly wait: (delayMs: number) => Promise<void>

  constructor(options: ApplicationApiClientOptions = {}) {
    this.projectionScope = options.chatIntentScope
    this.scope = options.chatIntentScope ?? {
      userId: 'volatile-user',
      workspaceId: 'volatile-workspace',
      projectId: 'volatile-project',
      sessionBinding: crypto.randomUUID(),
    }
    this.agentChatRetryDelaysMs = options.agentChatRetryDelaysMs
      ?? DEFAULT_AGENT_CHAT_RETRY_DELAYS_MS
    this.wait = options.wait
      ?? ((delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)))
    this.chatIntents = new ChatIntentJournal({
      scope: this.scope,
      storage: options.chatIntentStorage,
      now: options.now,
      ttlMs: options.chatIntentTtlMs,
      capacity: options.chatIntentCapacity,
      pruneForeignPartitions: options.pruneForeignChatIntentPartitions,
    })
    this.mutationIntents = new MutationIntentJournal({
      scope: this.scope,
      storage: options.chatIntentStorage,
      now: options.now,
      ttlMs: options.chatIntentTtlMs,
      capacity: options.chatIntentCapacity,
      pruneForeignPartitions: options.pruneForeignChatIntentPartitions,
    })
  }

  async loadProjection(): Promise<RemoteProjectionSnapshot> {
    const envelope = await projectionFetch()
    const scope = record(envelope.scope)
    const projectedUserId = boundedText(scope.user_id, 512)
    const projectedWorkspaceId = boundedText(scope.workspace_id, 512)
    const projectedProjectId = boundedText(scope.project_id, 512)
    if (!projectedUserId || !projectedWorkspaceId || !projectedProjectId) {
      throw new Error('Arena application projection scope is invalid')
    }
    if (
      this.projectionScope && (
        projectedUserId !== this.projectionScope.userId ||
        projectedWorkspaceId !== this.projectionScope.workspaceId ||
        projectedProjectId !== this.projectionScope.projectId
      )
    ) {
      throw new Error('Arena application projection scope mismatch')
    }
    const snapshotId = boundedText(envelope.snapshot_id, 128)
    const capturedAt = boundedText(envelope.captured_at, 64)
    if (!snapshotId || !capturedAt || !Number.isFinite(Date.parse(capturedAt))) {
      throw new Error('Arena application projection identity is invalid')
    }
    const data = record(envelope.data)
    const rawRuns = projectedArray(data, 'runs', 2_000)
    const rawQueue = projectedArray(data, 'queue_entries', 2_000)
    const rawTasks = projectedArray(data, 'tasks', 2_000)
    const rawKnowledge = projectedArray(data, 'knowledge', 2_000)
    const rawAgents = projectedArray(data, 'agents', 2_000)
    const rawChats = projectedArray(data, 'chats', 1_000)
    const rawInteractions = projectedArray(data, 'interactions', 2_000)

    const nextActions = new Map<string, ReadonlySet<string>>()
    const nextChatBindings = new Map<string, { agentId?: string; revision: number }>()
    const nextChatControlPolicies = new Map<string, AllowedApplicationChatControls>()
    const runs = rawRuns.map((value) => {
      const item = resource(value)
      nextActions.set(item.id, new Set(item.allowed_actions))
      return normalizeRun(value)
    })
    const tasks = rawTasks.map((value) => {
      const item = resource(value)
      nextActions.set(`subject:task:${item.id}`, new Set(item.allowed_actions))
      return normalizeTask(value)
    })
    const chats = rawChats.map((value) => {
      const item = resource(value)
      const chat = normalizeChat(value)
      nextActions.set(`chat:${chat.id}`, new Set(item.allowed_actions))
      nextChatBindings.set(chat.id, { agentId: chat.agentId, revision: chat.revision })
      if (chat.allowedControls) nextChatControlPolicies.set(chat.id, chat.allowedControls)
      return chat
    })
    const interactions = rawInteractions.map((value) => {
      const item = resource(value)
      nextActions.set(item.id, new Set(item.allowed_actions))
      return normalizeInteraction(value)
    })
    const health = normalizeDaemonHealth(data.operation)
    const queue = rawQueue.map(normalizeQueueEntry)
    const knowledge = rawKnowledge.map(normalizeKnowledge)
    const agents = rawAgents.map((value) => {
      const item = resource(value)
      const agent = normalizeAgent(value)
      nextActions.set(`agent:${agent.id}`, new Set(item.allowed_actions))
      return agent
    }).filter((agent) => agent.id.length > 0)

    // Commit capability/binding state only after the entire replacement has
    // validated. A malformed late row must not damage the last-good authority
    // maps retained alongside the last-good visual store snapshot.
    this.actions.clear()
    for (const [key, value] of nextActions) this.actions.set(key, value)
    this.chatBindings.clear()
    for (const [key, value] of nextChatBindings) this.chatBindings.set(key, value)
    this.chatControlPolicies.clear()
    for (const [key, value] of nextChatControlPolicies) this.chatControlPolicies.set(key, value)
    return {
      snapshotId,
      capturedAt,
      health,
      runs,
      queue,
      tasks,
      knowledge,
      agents,
      chats,
      interactions,
    }
  }

  async getDaemonHealth(): Promise<DaemonHealth> {
    const envelope = await applicationFetch('/operations')
    return normalizeDaemonHealth(record(envelope.data).operation)
  }

  async listRuns(limit = 40): Promise<NormalizedRun[]> {
    const query = new URLSearchParams({ limit: String(limit) })
    const envelope = await applicationFetch(`/runs?${query}`)
    const runs = record(envelope.data).runs
    if (!Array.isArray(runs)) return []
    return runs.map((value) => {
      const item = resource(value)
      this.actions.set(item.id, new Set(item.allowed_actions))
      return normalizeRun(value)
    })
  }

  async listAgents(): Promise<ProjectedAgent[]> {
    const envelope = await applicationFetch('/agents')
    const agents = record(envelope.data).agents
    if (!Array.isArray(agents)) return []
    return agents
      .map((value) => {
        const item = resource(value)
        const agent = normalizeAgent(value)
        this.actions.set(`agent:${agent.id}`, new Set(item.allowed_actions))
        return agent
      })
      .filter((agent) => agent.id.length > 0)
  }

  async getAgentDetail(id: string): Promise<ProjectedAgentDetail> {
    const envelope = await applicationFetch(`/agents/${encodeURIComponent(id)}`)
    const item = resource(record(envelope.data).agent)
    if (item.type !== 'agent' || !item.id || item.id !== id) {
      throw new Error('Invalid agent detail projection')
    }
    const attributes = item.attributes ?? {}
    const canInspect = item.allowed_actions?.includes('inspect') ?? false
    const canStartChat = item.allowed_actions?.includes('start_chat') ?? false
    this.actions.set(`agent:${id}`, new Set(item.allowed_actions))
    const rawConfiguration = record(attributes.configuration)
    const hasConfiguration = canInspect && Object.keys(rawConfiguration).length > 0
    const memory = record(rawConfiguration.memory)
    const communication = record(rawConfiguration.communication)
    const toolPolicy = record(rawConfiguration.tool_policy)
    return {
      id: item.id,
      name: string(attributes.name, item.id),
      ...(canStartChat ? { canStartChat: true } : {}),
      canInspect,
      ...(hasConfiguration
        ? {
            configuration: {
              model: optionalString(rawConfiguration.model),
              tool: optionalString(rawConfiguration.tool),
              provider: optionalString(rawConfiguration.provider),
              systemPrompt: optionalString(rawConfiguration.system_prompt),
              mcpServers: stringArray(rawConfiguration.mcp_servers),
              description: optionalString(rawConfiguration.description),
              skills: stringArray(rawConfiguration.skills),
              ...(typeof memory.enabled === 'boolean'
                ? { memory: { enabled: memory.enabled } }
                : {}),
              ...(typeof communication.enabled === 'boolean'
                ? {
                    communication: {
                      enabled: communication.enabled,
                      channels: stringArray(communication.channels),
                    },
                  }
                : {}),
              reasoningEffort: optionalString(rawConfiguration.reasoning_effort),
              maxTokens:
                typeof rawConfiguration.max_tokens === 'number' &&
                Number.isFinite(rawConfiguration.max_tokens)
                  ? rawConfiguration.max_tokens
                  : undefined,
              temperature:
                typeof rawConfiguration.temperature === 'number' &&
                Number.isFinite(rawConfiguration.temperature)
                  ? rawConfiguration.temperature
                  : undefined,
              ...(Object.keys(toolPolicy).length > 0
                ? {
                    toolPolicy: {
                      allow: stringArray(toolPolicy.allow),
                      deny: stringArray(toolPolicy.deny),
                    },
                  }
                : {}),
            },
          }
        : {
            redactionReason: canInspect
              ? 'configuration_unavailable' as const
              : 'inspection_not_authorized' as const,
          }),
    }
  }

  async listChats(): Promise<ProjectedChat[]> {
    const result: ProjectedChat[] = []
    const ids = new Set<string>()
    const cursors = new Set<string>()
    let cursor: string | undefined
    do {
      const query = new URLSearchParams({ limit: String(PROJECTED_CHAT_PAGE_SIZE) })
      if (cursor) query.set('cursor', cursor)
      const envelope = await applicationFetch(`/chats?${query}`)
      const data = record(envelope.data)
      if (!Array.isArray(data.chats)) throw new Error('Invalid chat collection projection')
      if (data.chats.length > PROJECTED_CHAT_PAGE_SIZE) {
        throw new Error('Chat catalog page exceeded its safety bound')
      }
      for (const value of data.chats) {
        const item = resource(value)
        const chat = normalizeChat(value)
        if (ids.has(chat.id)) throw new Error('Duplicate chat projection')
        ids.add(chat.id)
        result.push(chat)
        this.actions.set(`chat:${chat.id}`, new Set(item.allowed_actions))
      this.chatBindings.set(chat.id, { agentId: chat.agentId, revision: chat.revision })
      if (chat.allowedControls) this.chatControlPolicies.set(chat.id, chat.allowedControls)
      else this.chatControlPolicies.delete(chat.id)
      }
      if (result.length > MAX_PROJECTED_CHAT_CATALOG) {
        throw new Error('Chat catalog pagination exceeded its safety bound')
      }
      const next = boundedText(data.next_cursor, 512)
      if (!next) return result
      if (cursors.has(next) || result.length >= MAX_PROJECTED_CHAT_CATALOG) {
        throw new Error('Chat catalog pagination exceeded its safety bound')
      }
      cursors.add(next)
      cursor = next
    } while (result.length < MAX_PROJECTED_CHAT_CATALOG)
    return result
  }

  async getChat(id: string): Promise<ProjectedChat> {
    if (!id || id.length > 256 || id.includes('/')) throw new Error('Invalid chat id')
    const envelope = await applicationFetch(`/chats/${encodeURIComponent(id)}`)
    const value = record(envelope.data).chat
    const item = resource(value)
    const chat = normalizeChat(value)
    if (chat.id !== id) throw new Error('Invalid chat detail projection')
    this.actions.set(`chat:${id}`, new Set(item.allowed_actions))
    this.chatBindings.set(id, { agentId: chat.agentId, revision: chat.revision })
    if (chat.allowedControls) this.chatControlPolicies.set(id, chat.allowedControls)
    else this.chatControlPolicies.delete(id)
    return chat
  }

  async startAgentChat(id: string): Promise<ProjectedChat> {
    if (!id || id.length > 256 || id.includes('/')) throw new Error('Invalid agent id')
    if (!this.actions.get(`agent:${id}`)?.has('start_chat')) {
      throw new Error('Agent chat start is not allowed')
    }
    const requestBody = '{}'
    const intent = await this.mutationIntents.claim(
      'agent_chat_start',
      id,
      requestBody,
    )
    const retryDelays = [0, ...this.agentChatRetryDelaysMs]
    let lastError: unknown
    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      const delayMs = retryDelays[attempt]
      if (delayMs > 0) await this.wait(delayMs)
      try {
        const envelope = await applicationFetch(
          `/agents/${encodeURIComponent(id)}/chats`,
          {
            method: 'POST',
            headers: {
              'Idempotency-Key': intent.idempotencyKey,
              'X-Correlation-Id': crypto.randomUUID(),
            },
            body: requestBody,
          },
        )
        const value = record(envelope.data).chat
        const item = resource(value)
        const chat = normalizeChat(value)
        if (
          item.type !== 'chat' ||
          !chat.id ||
          !chat.canSend ||
          chat.agentId !== id
        ) throw new Error('Invalid agent chat projection')
        this.actions.set(`chat:${chat.id}`, new Set(item.allowed_actions))
        this.chatBindings.set(chat.id, { agentId: chat.agentId, revision: chat.revision })
        if (chat.allowedControls) this.chatControlPolicies.set(chat.id, chat.allowedControls)
        await this.mutationIntents.clear(intent)
        return chat
      } catch (error) {
        lastError = error
        if (
          isAgentChatConfirmationPending(error) &&
          attempt + 1 < retryDelays.length
        ) continue
        if (isDefinitiveMutationFailure(error)) await this.mutationIntents.clear(intent)
        throw error
      }
    }
    throw lastError
  }

  async sendChatMessage(
    id: string,
    message: string,
    controls?: ApplicationChatControls,
  ): Promise<ChatSendResult> {
    if (!id || id.length > 256 || id.includes('/')) throw new Error('Invalid chat id')
    const normalized = normalizeChatSendText(message)
    if (!normalized) throw new Error('Invalid chat message')
    if (!this.actions.get(`chat:${id}`)?.has('send')) {
      throw new Error('Chat send is not allowed')
    }
    const observedBinding = this.chatBindings.get(id)
    if (!observedBinding) throw new Error('Canonical chat binding is unavailable')
    const authorizedControls = authorizedChatControls(controls, this.chatControlPolicies.get(id))
    const serializedControls = authorizedControls ? JSON.stringify(authorizedControls) : undefined
    const intent = await this.chatIntents.claim({
      conversationId: id,
      agentId: observedBinding.agentId,
      revision: observedBinding.revision,
    }, normalized, serializedControls)
    // A reload may observe the revision reserved by this exact operation. The
    // terminal receipt is therefore checked against the original durable
    // binding, not mutable metadata fetched after the uncertain attempt.
    const binding = { agentId: intent.agentId, revision: intent.revision }
    try {
      const envelope = await applicationFetch(
        `/chats/${encodeURIComponent(id)}/messages`,
        {
          method: 'POST',
          headers: {
            'Idempotency-Key': intent.idempotencyKey,
            'X-Correlation-Id': crypto.randomUUID(),
          },
          body: JSON.stringify({
            message: normalized,
            ...(authorizedControls ? { controls: authorizedControls } : {}),
          }),
        },
      )
      const data = record(envelope.data)
      const item = resource(data.chat)
      const chat = normalizeChat(data.chat)
      const turn = canonicalChatTurn(data.turn, binding)
      const sent = turn
        ? canonicalReceiptMessage(
            chat,
            normalizeChatMessage(data.message),
            turn.status === 'completed'
              ? { id: turn.messageId!, seq: turn.seq!, role: 'assistant' }
              : { id: turn.userMessageId, seq: turn.userSeq, role: 'user' },
          )
        : null
      if (
        chat.id !== id ||
        !chat.canSend ||
        chat.agentId !== binding.agentId ||
        chat.revision < binding.revision ||
        !turn ||
        !sent
      ) {
        throw new Error('Invalid chat send projection')
      }
      this.actions.set(`chat:${id}`, new Set(item.allowed_actions))
      this.chatBindings.set(id, { agentId: chat.agentId, revision: chat.revision })
      if (chat.allowedControls) this.chatControlPolicies.set(id, chat.allowedControls)
      else this.chatControlPolicies.delete(id)
      await this.chatIntents.clear(intent)
      return { chat, message: sent, turn }
    } catch (error) {
      // Only the server's exact conflict outcome proves this caller key cannot
      // reconcile the intended request. Network errors, in-progress responses,
      // 5xx, and other non-terminal outcomes retain the durable key.
      if (
        error instanceof ApplicationHttpError &&
        error.status === 409 &&
        error.code === 'idempotency_conflict'
      ) {
        await this.chatIntents.clear(intent)
      }
      throw error
    }
  }

  async getRun(id: string): Promise<NormalizedRun> {
    const envelope = await applicationFetch(`/runs/${encodeURIComponent(id)}`)
    const value = record(envelope.data).run
    const item = resource(value)
    this.actions.set(item.id, new Set(item.allowed_actions))
    return normalizeRun(value)
  }

  streamTranscript(
    runId: string,
    onEvent: (event: TranscriptEvent) => void,
    onError?: (error: Error) => void,
    cursor?: string,
    onReset?: () => void,
  ): StreamHandle {
    const controller = new AbortController()
    void this.consumeEvents(
      runId,
      onEvent,
      cursor,
      controller.signal,
      onReset,
    ).catch((cause) => {
      if (!controller.signal.aborted) {
        onError?.(cause instanceof Error ? cause : new Error(String(cause)))
      }
    })
    return { cancel: () => controller.abort() }
  }

  async listTasks(): Promise<Task[]> {
    const envelope = await applicationFetch('/subjects?kind=task&limit=100')
    const subjects = record(envelope.data).subjects
    return Array.isArray(subjects)
      ? subjects.map((value) => {
          const item = resource(value)
          this.actions.set(`subject:task:${item.id}`, new Set(item.allowed_actions))
          return normalizeTask(value)
        })
      : []
  }

  async launchSubjectRun(
    kind: string,
    id: string,
    workflowRef?: string,
  ): Promise<NormalizedRun> {
    if (!/^[a-z][a-z0-9_-]{0,63}$/.test(kind)) {
      throw new Error('Invalid subject kind')
    }
    if (!id || id.length > 256 || id.includes('/')) {
      throw new Error('Invalid subject id')
    }
    if (!this.actions.get(`subject:${kind}:${id}`)?.has('launch')) {
      throw new Error('Subject launch is not allowed')
    }
    const normalizedWorkflowRef = workflowRef?.trim()
    if (normalizedWorkflowRef !== undefined && (
      normalizedWorkflowRef.length === 0 || normalizedWorkflowRef.length > 256
    )) {
      throw new Error('Invalid workflow reference')
    }
    const requestBody = JSON.stringify(normalizedWorkflowRef
      ? { workflow_ref: normalizedWorkflowRef }
      : {})
    const intent = await this.mutationIntents.claim(
      'subject_launch',
      `${kind}:${id}:${normalizedWorkflowRef ?? ''}`,
      requestBody,
    )
    try {
      const envelope = await applicationFetch(
        `/subjects/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/runs`,
        {
          method: 'POST',
          headers: {
            'Idempotency-Key': intent.idempotencyKey,
            'X-Correlation-Id': crypto.randomUUID(),
          },
          body: requestBody,
        },
      )
      const value = record(envelope.data).run
      const item = resource(value)
      const run = normalizeRun(value)
      const expectedSubjectIds = new Set([id, `${kind}:${id}`])
      if (
        item.type !== 'run' ||
        !run.id ||
        !expectedSubjectIds.has(run.taskId)
      ) {
        throw new Error('Invalid launched run projection')
      }
      this.actions.set(item.id, new Set(item.allowed_actions))
      await this.mutationIntents.clear(intent)
      return run
    } catch (error) {
      // Preserve the key only for an uncertain outcome. A retry after a known
      // caller/policy/conflict response represents a new intent.
      if (isDefinitiveMutationFailure(error)) await this.mutationIntents.clear(intent)
      throw error
    }
  }

  async listKnowledge(): Promise<KnowledgeEntry[]> {
    const envelope = await applicationFetch('/subjects?kind=knowledge&limit=100')
    const subjects = record(envelope.data).subjects
    return Array.isArray(subjects)
      ? subjects.map(normalizeKnowledge)
      : []
  }

  async listQueue(): Promise<QueueEntry[]> {
    const envelope = await applicationFetch('/queue')
    const entries = record(envelope.data).queue_entries
    return Array.isArray(entries)
      ? entries.map(normalizeQueueEntry)
      : []
  }

  async listInteractions(runId: string): Promise<Interaction[]> {
    const envelope = await applicationFetch('/interactions')
    const data = record(envelope.data)
    const items = Array.isArray(data.interactions) ? data.interactions : []
    return items
      .map((value) => {
        const item = resource(value)
        this.actions.set(item.id, new Set(item.allowed_actions))
        return normalizeInteraction(value)
      })
      .filter((interaction) => !runId || interaction.runId === runId)
  }

  async respondToInteraction(
    interactionId: string,
    answer: InteractionAnswer,
  ): Promise<void> {
    if (!this.actions.get(interactionId)?.has('respond')) {
      throw new Error('Interaction response is not allowed')
    }
    const response =
      answer.kind === 'approval'
        ? { allow: answer.allow }
        : answer.kind === 'structured'
          ? { selects: answer.selects }
          : { text: answer.text }
    const requestBody = JSON.stringify(response)
    const intent = await this.mutationIntents.claim(
      'interaction_response',
      interactionId,
      requestBody,
    )
    try {
      await applicationFetch(
        `/interactions/${encodeURIComponent(interactionId)}/responses`,
        {
          method: 'POST',
          headers: {
            'Idempotency-Key': intent.idempotencyKey,
            'X-Correlation-Id': crypto.randomUUID(),
          },
          body: requestBody,
        },
      )
      await this.mutationIntents.clear(intent)
    } catch (error) {
      if (isDefinitiveMutationFailure(error)) await this.mutationIntents.clear(intent)
      throw error
    }
  }

  async getMe(): Promise<Record<string, unknown>> {
    const response = await fetch('/api/v1/session', { credentials: 'same-origin' })
    if (!response.ok) throw new Error(`GET /api/v1/session → ${response.status}`)
    return response.json() as Promise<Record<string, unknown>>
  }

  async pollRemoteEvents(cursor?: string): Promise<RemoteEventPage> {
    const query = new URLSearchParams({ limit: '100' })
    if (cursor) query.set('cursor', cursor)
    const envelope = await applicationFetch(`/events?${query}`)
    const data = record(envelope.data)
    return {
      cursor: string(data.next_cursor) || cursor,
      changed: Array.isArray(data.events) && data.events.length > 0,
      ...(data.has_more === true ? { hasMore: true } : {}),
    }
  }

  private async consumeEvents(
    runId: string,
    onEvent: (event: TranscriptEvent) => void,
    cursor: string | undefined,
    signal: AbortSignal,
    onReset?: () => void,
  ): Promise<void> {
    if (!this.actions.get(runId)?.has('stream')) {
      throw new Error('Run streaming is not allowed')
    }
    const grantEnvelope = await applicationFetch(
      `/runs/${encodeURIComponent(runId)}/stream-grants`,
      { method: 'POST', body: '{}' },
    )
    const grantData = record(grantEnvelope.data)
    const grant = string(grantData.grant ?? grantData.token)
    if (!grant) throw new Error('Portal stream grant is missing')
    const query = new URLSearchParams({ grant })
    if (cursor) query.set('cursor', cursor)
    const response = await fetch(
      `${BASE}/runs/${encodeURIComponent(runId)}/events?${query}`,
      { credentials: 'same-origin', signal, headers: { Accept: 'text/event-stream' } },
    )
    if (!response.ok || !response.body) throw new Error(`Run events → ${response.status}`)
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffered = ''
    while (!signal.aborted) {
      const { value, done } = await reader.read()
      if (done) break
      buffered += decoder.decode(value, { stream: true })
      const extracted = extractSseFrames(buffered)
      buffered = extracted.remainder
      for (const frame of extracted.frames) {
        const data = frame
          .split(/\r?\n/)
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n')
        if (data) {
          const payload = JSON.parse(data) as Envelope
          if (payload.schema !== 'portal.application.v1') {
            throw new Error('Portal event schema mismatch')
          }
          if (payload.reset) onReset?.()
          for (const event of payload.events ?? []) {
            onEvent(eventToTranscript(event, runId))
          }
        }
      }
    }
  }
}
